import { Observable } from "rxjs";
import { HttpClient } from "src/infra/http/client";
import { Environment } from "src/libs/environment";
import { inject, injectable } from "tsyringe";
import { Token } from "src/containers/authentication/models/token";
import { AuthEndpoints } from "src/constants/endpoints";
import type { GenericSuccessResponse } from "src/infra/http/types";
import { autoBind } from "src/shared/decorators/autobind";
import { DI } from "src/infra/di/container";

@injectable()
export class AuthenticationRepository {
    static key = "AuthenticationRepository";

    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    getTokenConventional(authCode: string): Observable<object> {
        return this.http.post<object>(AuthEndpoints.AUTH_GET_TOKEN, {
            data: { code: authCode },
            customHeader: { "Content-Type": "application/json" },
        });
    }

    @autoBind
    getTokenSSO(authCode: string): Observable<object> {
        const headers = this.http.httpUtils.getMsBaseHeaders(
            window.userIdentity,
            "MEP",
            Environment.string("UX_APIM_OCP_MANAGEMENT_ACCESS_KEY")
        );
        return this.http.post<object>(AuthEndpoints.AUTH_GET_TOKEN_SSO, {
            host: this.http.hostAccessManagementEnv,
            customHeader: {
                ...headers,
                Authorization: `${authCode}`,
                "device-id": "web",
                "Content-Type": "application/json",
            },
        });
    }

    @autoBind
    validateToken(bearer: string): Observable<object> {
        const headers = this.http.httpUtils.getMsBaseHeaders(
            window.userIdentity,
            "MEP",
            Environment.string("UX_APIM_OCP_MANAGEMENT_ACCESS_KEY")
        );
        return this.http.get<object>(AuthEndpoints.AUTH_VALIDATE_TOKEN_SSO, {
            host: this.http.hostAccessManagementEnv,
            customHeader: {
                ...headers,
                Authorization: bearer,
                "device-id": "web",
                "Content-Type": "application/json",
            },
        });
    }

    @autoBind
    refreshToken(bearer: string): Observable<object> {
        return this.http.get<object>(AuthEndpoints.SESSION_TOKEN, {
            customHeader: {
                "Content-Type": "application/json",
                Authorization: bearer,
            },
        });
    }

    @autoBind
    me(token: Token, userIdentity?: string): Observable<object> {
        const headers = this.http.httpUtils.getMsBaseHeaders(
            userIdentity,
            "MEP"
        );
        return this.http.getJSON<object>(AuthEndpoints.AUTH_ME, {
            customHeader: {
                ...headers,
                "Content-Type": "application/json",
                Authorization: token.value,
            },
        });
    }

    @autoBind
    logout(bearer: string): Observable<GenericSuccessResponse> {
        return this.http.delete<GenericSuccessResponse>(
            AuthEndpoints.SESSION_TOKEN,
            {
                customHeader: {
                    "Content-Type": "application/json",
                    Authorization: bearer,
                },
            }
        );
    }

    @autoBind
    logoutSSO(bearer: string): Observable<GenericSuccessResponse> {
        const headers = this.http.httpUtils.getMsBaseHeaders(
            window.userIdentity,
            "MEP",
            Environment.string("UX_APIM_OCP_MANAGEMENT_ACCESS_KEY")
        );
        return this.http.delete<GenericSuccessResponse>(
            AuthEndpoints.AUTH_REVOKE_TOKEN_SSO,
            {
                host: this.http.hostAccessManagementEnv,
                customHeader: {
                    ...headers,
                    Authorization: `${bearer}`,
                    "device-id": "web",
                    "Content-Type": "application/json",
                },
            }
        );
    }
}

DI.put(AuthenticationRepository.key, AuthenticationRepository);
